import React, { useEffect, useRef } from 'react';

import type { VgpsSession } from '../core/runtime/types';

/**
 * Snapshot of the DOM elements rendering the live media.
 *
 * `videoElement` is always non-null (the `<video>` is always rendered, even
 * when the WASM path is active). `overlayCanvas` is only set when the consumer
 * opts in via `showOverlayCanvas`.
 *
 * Consumers that need to attach custom controls (mute, screenshot, etc.) should
 * grab these via `onMediaElements` rather than querying the DOM, because the
 * internal markup of the view may change between SDK versions.
 */
export interface VgpsVideoMediaElements {
  videoElement: HTMLVideoElement | null;
  overlayCanvas: HTMLCanvasElement | null;
}

export interface VgpsVideoViewProps {
  session: VgpsSession | null;
  className?: string;
  style?: React.CSSProperties;
  muted?: boolean;
  playsInline?: boolean;
  showOverlayCanvas?: boolean;
  /**
   * Fires whenever the underlying `<video>` / overlay `<canvas>` elements
   * mount or unmount. Use this instead of `querySelector` to attach custom
   * UI (mute / screenshot / fullscreen) without depending on the SDK's
   * internal DOM structure.
   *
   * The callback is invoked synchronously after each ref update.
   */
  onMediaElements?: (elements: VgpsVideoMediaElements) => void;
}

export function VgpsVideoView({
  session,
  className,
  style,
  muted = true,
  playsInline = true,
  showOverlayCanvas = false,
  onMediaElements,
}: VgpsVideoViewProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const setVideoRef = (el: HTMLVideoElement | null): void => {
    videoRef.current = el;
    onMediaElements?.({ videoElement: el, overlayCanvas: canvasRef.current });
  };
  const setCanvasRef = (el: HTMLCanvasElement | null): void => {
    canvasRef.current = el;
    onMediaElements?.({ videoElement: videoRef.current, overlayCanvas: el });
  };

  useEffect(() => {
    if (!session || !videoRef.current) {
      return undefined;
    }

    void session.attachRenderSurface({
      videoElement: videoRef.current,
      overlayCanvas: canvasRef.current,
      muted,
      playsInline,
      backendPreference: 'auto',
    });

    return () => {
      void session.detachRenderSurface();
    };
  }, [session, muted, playsInline]);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        ...style,
      }}
    >
      <video
        ref={setVideoRef}
        muted={muted}
        playsInline={playsInline}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#101820',
          pointerEvents: 'none', // GPU compositor layer in Chrome can intercept events
        }}
      />
      {/* Transparent shield above the video layer so Chrome's GPU compositor
          does not capture pointer events before they reach sibling overlay divs. */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
      {showOverlayCanvas ? (
        <canvas
          ref={setCanvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  );
}